import { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function StudentRegistration() {
  const { t } = useTranslation('studentregistration');
  const { api, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [classes, setClasses] = useState([]);
  const [streams, setStreams] = useState([]);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    gender: 'Male',
    date_of_birth: '',
    date_of_joining: '',
    address: '',
    admission_number: '',
    class_id: '',
    stream_id: '',
    parent_name: '',
    parent_phone: '',
    parent_id_number: '',
    parent_address: '',
    parent_email: '',
    relation_type: 'Father'
  });

  // Fetch classes and streams on component mount
  useEffect(() => {
    fetchClassesAndStreams();
  }, []);

  // Update available streams when class_id changes
  useEffect(() => {
    if (formData.class_id) {
      const selectedClass = classes.find(c => c.id === parseInt(formData.class_id));
      if (selectedClass) {
        setStreams(selectedClass.streams);
        setFormData(prev => ({
          ...prev,
          stream_id: '' // Reset stream selection when class changes
        }));
      }
    } else {
      setStreams([]);
    }
  }, [formData.class_id, classes]);

  const fetchClassesAndStreams = async () => {
    setIsLoadingClasses(true);
    try {
      const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/classes`);
      setClasses(response.data);
    } catch (error) {
      console.error("Error fetching classes:", error);
      setGeneralError(t('errors.loadClassesError'));
    } finally {
      setIsLoadingClasses(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear specific field error when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    // Student validation
    if (!formData.name.trim()) errors.name = t('errors.nameRequired');
    if (!formData.date_of_birth) errors.date_of_birth = t('errors.dobRequired');
    if (!formData.date_of_joining) errors.date_of_joining = t('errors.dojRequired');
    if (!formData.class_id) errors.class_id = t('errors.classRequired');
    if (!formData.stream_id) errors.stream_id = t('errors.streamRequired');

    // Parent validation
    if (!formData.parent_name.trim()) errors.parent_name = t('errors.parentNameRequired');
    if (!formData.parent_phone.trim()) errors.parent_phone = t('errors.parentPhoneRequired');
    if (!formData.parent_email.trim()) errors.parent_email = t('errors.parentEmailRequired');
    else if (!/\S+@\S+\.\S+/.test(formData.parent_email)) errors.parent_email = t('errors.invalidEmail');
    if (!formData.parent_address.trim()) errors.parent_address = t('errors.parentAddressRequired');
    if (!formData.relation_type) errors.relation_type = t('errors.relationshipRequired');

    // Validate parent_id_number - it should be either a valid number or empty
    if (formData.parent_id_number && !/^\d+$/.test(formData.parent_id_number)) {
      errors.parent_id_number = t('errors.invalidIdNumber');
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');
    setFormErrors({});

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Prepare data for API submission
    const submissionData = { ...formData };

    // Convert numeric strings to integers or set to null if empty
    if (submissionData.admission_number) {
      submissionData.admission_number = parseInt(submissionData.admission_number, 10);
    } else {
      submissionData.admission_number = null;
    }

    if (submissionData.parent_id_number) {
      submissionData.parent_id_number = parseInt(submissionData.parent_id_number, 10);
    } else {
      // If parent_id_number is empty, set it to null instead of sending empty string
      submissionData.parent_id_number = null;
    }

    submissionData.class_id = parseInt(submissionData.class_id, 10);
    submissionData.stream_id = parseInt(submissionData.stream_id, 10);

    setIsLoading(true);

    try {
      await api.post(
        `/api/v1/admin/schools/${user.school_reg}/students`,
        submissionData
      );

      setSuccess(true);
      // Reset form after successful submission
      setFormData({
        name: '',
        gender: 'Male',
        date_of_birth: '',
        date_of_joining: '',
        address: '',
        admission_number: '',
        class_id: '',
        stream_id: '',
        parent_name: '',
        parent_phone: '',
        parent_id_number: '',
        parent_address: '',
        parent_email: '',
        relation_type: 'Father'
      });

      // Show success message for 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);

    } catch (error) {
      console.error("Registration error:", error);

      // Handle different types of API errors
      if (error.response) {
        const { data } = error.response;

        if (data.detail) {
          if (typeof data.detail === 'string') {
            setGeneralError(data.detail);
          } else if (Array.isArray(data.detail)) {
            // Handle FastAPI validation errors
            const fieldErrors = {};

            data.detail.forEach(err => {
              // Extract the field name from the location array
              const fieldPath = err.loc;
              const field = fieldPath[fieldPath.length - 1];

              // Map backend field names to form field names if needed
              const fieldName = field;

              // Create user-friendly error messages
              let errorMessage = err.msg;

              // Specific error handling for common FastAPI validation errors
              if (err.type === 'int_parsing') {
                if (field === 'parent_id_number') {
                  errorMessage = t('errors.invalidIdNumber');
                } else if (field === 'admission_number') {
                  errorMessage = t('errors.invalidAdmissionNumber');
                } else {
                  errorMessage = `${field.replace('_', ' ')} must be a valid number`;
                }
              }

              fieldErrors[fieldName] = errorMessage;
            });

            setFormErrors(fieldErrors);

            // If there are validation errors but no visible errors set
            // (this can happen if the error is on a field we don't directly show)
            if (Object.keys(fieldErrors).length > 0 && !Object.values(fieldErrors).some(e => e)) {
              setGeneralError(t('errors.generalError'));
            }
          }
        } else if (data.errors) {
          setFormErrors(data.errors);
        } else {
          setGeneralError(t('errors.serverError'));
        }
      } else if (error.request) {
        setGeneralError(t('errors.noResponse'));
      } else {
        setGeneralError(t('errors.processError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md flex items-center text-green-700">
          <CheckCircle className="mr-2 h-5 w-5" />
          <span>{t('success')}</span>
        </div>
      )}

      {generalError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
          <AlertCircle className="mr-2 h-5 w-5" />
          <span>{generalError}</span>
        </div>
      )}

      <h2 className="text-2xl font-semibold text-gray-800 mb-6">{t('pageTitle')}</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Student Information Section */}
        <div className="border-b border-gray-200 pb-4">
          <h3 className="text-lg font-medium text-gray-700 mb-4">{t('studentInfo.sectionTitle')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Full Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                {t('studentInfo.fullName')} {t('form.required')}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${formErrors.name
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
                  }`}
                placeholder={t('studentInfo.fullName')}
              />
              {formErrors.name && (
                <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
              )}
            </div>

            {/* Gender */}
            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">
                {t('studentInfo.gender')} {t('form.required')}
              </label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-500"
              >
                <option value="Male">{t('studentInfo.male')}</option>
                <option value="Female">{t('studentInfo.female')}</option>
              </select>
            </div>

            {/* Admission Number (Optional) */}
            <div>
              <label htmlFor="admission_number" className="block text-sm font-medium text-gray-700 mb-1">
                {t('studentInfo.admissionNumber')}
              </label>
              <input
                type="number"
                id="admission_number"
                name="admission_number"
                value={formData.admission_number}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${formErrors.admission_number
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
                  }`}
                placeholder={t('studentInfo.admissionNumber')}
              />
              {formErrors.admission_number && (
                <p className="mt-1 text-sm text-red-600">{formErrors.admission_number}</p>
              )}
            </div>

            {/* Date of Birth */}
            <div>
              <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-1">
                {t('studentInfo.dateOfBirth')} {t('form.required')}
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="date_of_birth"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${formErrors.date_of_birth
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                      : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
                    }`}
                />
              </div>
              {formErrors.date_of_birth && (
                <p className="mt-1 text-sm text-red-600">{formErrors.date_of_birth}</p>
              )}
            </div>

            {/* Date of Joining */}
            <div>
              <label htmlFor="date_of_joining" className="block text-sm font-medium text-gray-700 mb-1">
                {t('studentInfo.dateOfJoining')} {t('form.required')}
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="date_of_joining"
                  name="date_of_joining"
                  value={formData.date_of_joining}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${formErrors.date_of_joining
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                      : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
                    }`}
                />
              </div>
              {formErrors.date_of_joining && (
                <p className="mt-1 text-sm text-red-600">{formErrors.date_of_joining}</p>
              )}
            </div>

            {/* Class */}
            <div>
              <label htmlFor="class_id" className="block text-sm font-medium text-gray-700 mb-1">
                {t('studentInfo.class')} {t('form.required')}
              </label>
              <select
                id="class_id"
                name="class_id"
                value={formData.class_id}
                onChange={handleChange}
                disabled={isLoadingClasses}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${formErrors.class_id
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
                  }`}
              >
                <option value="">{t('studentInfo.selectClass')}</option>
                {classes.map((classItem) => (
                  <option key={classItem.id} value={classItem.id}>
                    {classItem.name}
                  </option>
                ))}
              </select>
              {formErrors.class_id && (
                <p className="mt-1 text-sm text-red-600">{formErrors.class_id}</p>
              )}
            </div>

            {/* Stream */}
            <div>
              <label htmlFor="stream_id" className="block text-sm font-medium text-gray-700 mb-1">
                {t('studentInfo.stream')} {t('form.required')}
              </label>
              <select
                id="stream_id"
                name="stream_id"
                value={formData.stream_id}
                onChange={handleChange}
                disabled={!formData.class_id || isLoadingClasses}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${formErrors.stream_id
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
                  }`}
              >
                <option value="">{t('studentInfo.selectStream')}</option>
                {streams.map((stream) => (
                  <option key={stream.id} value={stream.id}>
                    {stream.name}
                  </option>
                ))}
              </select>
              {formErrors.stream_id && (
                <p className="mt-1 text-sm text-red-600">{formErrors.stream_id}</p>
              )}
            </div>
          </div>

          {/* Address (Optional) - Full width */}
          <div className="mt-6">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
              {t('studentInfo.address')}
            </label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows="2"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-500"
              placeholder={t('studentInfo.addressPlaceholder')}
            ></textarea>
          </div>
        </div>

        {/* Parent/Guardian Information Section */}
        <div className="pt-2">
          <h3 className="text-lg font-medium text-gray-700 mb-4">{t('parentInfo.sectionTitle')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Parent Full Name */}
            <div>
              <label htmlFor="parent_name" className="block text-sm font-medium text-gray-700 mb-1">
                {t('parentInfo.fullName')} {t('form.required')}
              </label>
              <input
                type="text"
                id="parent_name"
                name="parent_name"
                value={formData.parent_name}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${formErrors.parent_name
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
                  }`}
                placeholder={t('parentInfo.fullNamePlaceholder')}
              />
              {formErrors.parent_name && (
                <p className="mt-1 text-sm text-red-600">{formErrors.parent_name}</p>
              )}
            </div>

            {/* Relationship Type */}
            <div>
              <label htmlFor="relation_type" className="block text-sm font-medium text-gray-700 mb-1">
                {t('parentInfo.relationship')} {t('form.required')}
              </label>
              <select
                id="relation_type"
                name="relation_type"
                value={formData.relation_type}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${formErrors.relation_type
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
                  }`}
              >
                <option value="Father">{t('parentInfo.father')}</option>
                <option value="Mother">{t('parentInfo.mother')}</option>
                <option value="Guardian">{t('parentInfo.guardian')}</option>
              </select>
              {formErrors.relation_type && (
                <p className="mt-1 text-sm text-red-600">{formErrors.relation_type}</p>
              )}
            </div>

            {/* Parent Phone */}
            <div>
              <label htmlFor="parent_phone" className="block text-sm font-medium text-gray-700 mb-1">
                {t('parentInfo.phoneNumber')} {t('form.required')}
              </label>
              <input
                type="tel"
                id="parent_phone"
                name="parent_phone"
                value={formData.parent_phone}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${formErrors.parent_phone
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
                  }`}
                placeholder={t('parentInfo.phonePlaceholder')}
              />
              {formErrors.parent_phone && (
                <p className="mt-1 text-sm text-red-600">{formErrors.parent_phone}</p>
              )}
            </div>

            {/* Parent Email */}
            <div>
              <label htmlFor="parent_email" className="block text-sm font-medium text-gray-700 mb-1">
                {t('parentInfo.email')} {t('form.required')}
              </label>
              <input
                type="email"
                id="parent_email"
                name="parent_email"
                value={formData.parent_email}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${formErrors.parent_email
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
                  }`}
                placeholder={t('parentInfo.emailPlaceholder')}
              />
              {formErrors.parent_email && (
                <p className="mt-1 text-sm text-red-600">{formErrors.parent_email}</p>
              )}
            </div>

            {/* Parent ID Number (Optional) */}
            <div>
              <label htmlFor="parent_id_number" className="block text-sm font-medium text-gray-700 mb-1">
                {t('parentInfo.idNumber')}
              </label>
              <input
                type="number"
                id="parent_id_number"
                name="parent_id_number"
                value={formData.parent_id_number}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${formErrors.parent_id_number
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
                  }`}
                placeholder={t('parentInfo.idPlaceholder')}
              />
              {formErrors.parent_id_number && (
                <p className="mt-1 text-sm text-red-600">{formErrors.parent_id_number}</p>
              )}
            </div>

            {/* Parent Address */}
            <div className="md:col-span-2">
              <label htmlFor="parent_address" className="block text-sm font-medium text-gray-700 mb-1">
                {t('parentInfo.address')} {t('form.required')}
              </label>
              <textarea
                id="parent_address"
                name="parent_address"
                value={formData.parent_address}
                onChange={handleChange}
                rows="2"
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${formErrors.parent_address
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
                  }`}
                placeholder={t('parentInfo.addressPlaceholder')}
              ></textarea>
              {formErrors.parent_address && (
                <p className="mt-1 text-sm text-red-600">{formErrors.parent_address}</p>
              )}
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-teal-700 hover:bg-teal-800 text-white font-medium py-2.5 px-4 rounded-md transition duration-300 flex items-center justify-center"
            style={{ backgroundColor: '#0E5252' }}
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin mr-2 h-5 w-5" />
                {t('form.registering')}
              </>
            ) : (
              t('form.registerButton')
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default StudentRegistration;